import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { startPoll } from "@/lib/poll";
import { timeAgo, formatApiError } from "@/lib/format";
import { useProfileViewer } from "@/components/Layout";
import { UndertakeModal } from "@/components/undertake/UndertakeModal";
import { MyUndertakings } from "@/components/undertake/MyUndertakings";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

const SORTS = [
  { k: "recent", label: "recent" },
  { k: "random", label: "random" },
  { k: "provisions", label: "provisions" },
];

function SortMenu({ sort, onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative shrink-0">
      <button type="button" data-testid="undertake-sort-select" onClick={() => setOpen((v) => !v)} className="btn w-full sm:w-52 px-4 py-3 text-base flex items-center justify-between gap-3">
        <span>{SORTS.find((o) => o.k === sort)?.label}</span>
        <span style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }}>▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute z-40 left-0 right-0 mt-1 flex flex-col gap-1 p-1" style={{ background: "var(--bg-primary)" }} data-testid="undertake-sort-options">
            {SORTS.map((o) => (
              <button key={o.k} type="button" data-testid={`undertake-sort-${o.k}`} onClick={() => { onPick(o.k); setOpen(false); }} className="btn w-full px-4 py-2 text-sm" style={sort === o.k ? { background: "var(--accent)", color: "#121212" } : undefined}>
                {o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function PileItem({ u, i, onOpen, viewProfile }) {
  return (
    <li
      data-testid={`undertake-item-${u.id}`}
      className="fade-in-up cursor-pointer transition-colors duration-200"
      style={{ borderBottom: "1px solid var(--border)", background: u.held_by_me ? "var(--bg-secondary)" : "transparent", animationDelay: `${i * 40}ms` }}
      onClick={() => onOpen(u)}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-secondary)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = u.held_by_me ? "var(--bg-secondary)" : "transparent")}
    >
      <div className="py-6 px-4">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          {u.held_by_me && u.status === "undertaken" && (
            <span data-testid={`undertake-active-tag-${u.id}`} className="text-[10px] uppercase tracking-widest px-2 py-1" style={{ background: "var(--accent)", color: "#121212" }}>undertaken · click to continue</span>
          )}
          <span data-testid={`undertake-reward-${u.id}`} className="text-xs tracking-widest ml-auto" style={{ color: "var(--accent)" }}>{u.provisions} provisions</span>
        </div>
        <p className="long-text font-head font-light text-xl leading-relaxed" style={{ color: "var(--text-primary)" }}>{u.text}</p>
        <div className="flex items-center gap-3 mt-4">
          <button onClick={(e) => { e.stopPropagation(); viewProfile(u.author?.id); }} data-testid={`undertake-delegator-${u.id}`} className="text-xs opacity-70 hover:opacity-100" style={{ color: "var(--text-secondary)" }}>
            {u.author?.name || "unknown"}
          </button>
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>· {timeAgo(u.created_at)}</span>
        </div>
      </div>
    </li>
  );
}

export function UndertakePile() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recent");
  const [randomMap, setRandomMap] = useState({});
  const [selected, setSelected] = useState(null);
  const [taken, setTaken] = useState([]);
  const { viewProfile } = useProfileViewer();
  const sortRef = useRef(sort);
  useEffect(() => { sortRef.current = sort; }, [sort]);

  const shuffle = (list) => { const m = {}; list.forEach((x) => { m[x.id] = Math.random(); }); setRandomMap(m); };

  const load = useCallback(async (quiet) => {
    if (!quiet) setLoading(true);
    try {
      const { data } = await api.get("/undertakings");
      setItems(data);
      api.get("/undertakings/taken").then((r) => setTaken(r.data)).catch((e) => console.debug("taken load failed", e));
      setRandomMap((prev) => { const m = { ...prev }; data.forEach((x) => { if (m[x.id] === undefined) m[x.id] = Math.random(); }); return m; });
    } catch (e) { console.debug("undertakings load failed", e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); return startPoll(() => load(true), 8000); }, [load]);

  const open = async (u) => {
    try {
      const { data } = await api.post(`/undertakings/${u.id}/view`);
      setSelected(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      load();
    }
  };

  const sorted = [...items].sort((a, b) => {
    if (a.held_by_me !== b.held_by_me) return a.held_by_me ? -1 : 1;
    if (sort === "provisions") return (b.provisions || 0) - (a.provisions || 0);
    if (sort === "recent") return new Date(b.created_at) - new Date(a.created_at);
    return (randomMap[a.id] ?? 0) - (randomMap[b.id] ?? 0);
  });

  return (
    <div>
      <div className="flex items-center justify-end gap-4 mb-8">
        <SortMenu sort={sort} onPick={(k) => { if (k === "random") shuffle(items); setSort(k); }} />
        <button type="button" data-testid="undertake-refresh-btn" onClick={() => load()} title="Refresh" className="btn shrink-0 flex items-center justify-center w-[50px] h-[50px]">
          <RefreshCw size={18} strokeWidth={1.5} className={loading ? "animate-spin" : undefined} />
        </button>
      </div>
      {loading && items.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</p>
      ) : sorted.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="undertake-empty">Nothing to undertake yet.</p>
      ) : (
        <ul data-testid="undertake-list">
          {sorted.map((u, i) => <PileItem key={u.id} u={u} i={i} onOpen={open} viewProfile={viewProfile} />)}
        </ul>
      )}
      <MyUndertakings items={taken} onOpen={open} />
      {selected && <UndertakeModal item={selected} onClose={() => { setSelected(null); load(); }} />}
    </div>
  );
}
